// Portals: doors to other rooms (contracts section 9).
//
// A portal's geometry comes from the scene's manifest (`portals`, from the pipeline), or, for a build made before
// portals existed, from the room's link in rooms.json (`geometry`). Where it leads comes from the room's link in
// rooms.json (`to_room`, `to_spawn`), else from the manifest's defaults. A link with a `flag` is active only with
// `?<flag>=1` in the page URL (the live listening room's door, while the room behind it is a test room). A link marked
// `closed` (a room connection set, 2026-10-30) makes no portal.
//
// Going through: walking into the opening (the player's centre within TRIGGER_M of the wall plane, inside the
// opening's width, moving toward it), or clicking the door twice (select, then open) like other interactive
// objects. After arriving, a portal fires again only once the player has left its trigger zone.
//
// Floor portals (kind "floor", 2026-10-28: the hallway's floor opening with the ladder down, the Basement's ladder up):
// an area on the floor. Clicking or tapping the pick box over it (the opening and the ladder up to `height`) twice
// (select, open) goes through; walking into it does not (2026-10-30). The arrival point is the portal's spawn.
// Hovering or selecting one outlines its ladder (2026-10-29): the glb node named by the portal's `node` (manifest, or the
// rooms.json link's geometry), kept by the loader as a separate shape (opts.shapes) because the drawn ladder may be
// batched with other props.

import { BoxGeometry, Mesh, MeshBasicMaterial, Vector3, type Scene } from 'three';
import type { PortalLink, RoomInfo, RoomsFile } from '@lr3d/shared';
import type { Manifest, PortalInfo, V3 } from '../scene/manifest';

/** Player centre this close to the wall plane (m) triggers. The walkable area keeps it >= ~0.25-0.30 m away. */
const TRIGGER_M = 0.36;
/** Arrival: this far into the room in front of the portal when the manifest gives no spawn. */
const SPAWN_IN_M = 0.8;
/** Floor portal: default reach (player radius 0.25 + 0.06) when the walkable area ends at the portal's area. */
const FLOOR_REACH_M = 0.31;
/** Floor portal: default pick box height. */
const FLOOR_PICK_H = 1.0;
export const PORTAL_PREFIX = 'portal:';

export interface ActivePortal {
  id: string;
  /** interactive id (`portal:<id>`) */
  key: string;
  position: Vector3;
  normal: Vector3;
  /** along the wall, the viewer's right when facing the portal from inside the room */
  tangent: Vector3;
  size: [number, number];
  link: PortalLink;
  mesh: Mesh;
  marker: Mesh | null;
  /** floor portal: the area (three.js x, z) and the trigger distance from it */
  floor: { x0: number; z0: number; x1: number; z1: number; reach: number } | null;
  /** floor portal: the glb node (the ladder) its hover and selection outline go round, if any */
  node: string | null;
}

export interface Spawn { x: number; z: number; yaw: number }

/** Where a player arriving through portal `id` of `m` stands, facing into the room; null if the portal is unknown. */
export function portalSpawn(m: Manifest, id: string | undefined, info?: RoomInfo): Spawn | null {
  if (!id) return null;
  const p = m.portals?.find((x) => x.id === id) ?? geometryPortal(id, info);
  if (!p) return null;
  if (p.spawn) return { x: p.spawn.position[0], z: p.spawn.position[2], yaw: (p.spawn.yaw_deg * Math.PI) / 180 };
  const n = p.normal;
  return { x: p.position[0] + n[0] * SPAWN_IN_M, z: p.position[2] + n[2] * SPAWN_IN_M, yaw: Math.atan2(-n[0], -n[2]) };
}

function geometryPortal(id: string, info?: RoomInfo): PortalInfo | null {
  const g = info?.portals[id]?.geometry;
  return g ? { id, position: g.position as V3, normal: g.normal as V3, size: g.size,
    ...(g.kind ? { kind: g.kind } : {}), ...(g.area ? { area: g.area } : {}), ...(g.height ? { height: g.height } : {}),
    ...(g.reach ? { reach: g.reach } : {}), ...(g.node ? { node: g.node } : {}), ...(g.spawn ? { spawn: { position: g.spawn.position as V3, yaw_deg: g.spawn.yaw_deg } } : {}) } : null;
}

/**
 * glb nodes the loader keeps as shapes for this scene: the `node` of every floor portal in the manifest and in the
 * rooms.json geometry of every room on this scene (a room switch between two rooms of one build does not reload it).
 */
export function shapeNodes(m: Manifest, rooms: RoomsFile): Set<string> {
  const out = new Set<string>();
  for (const p of m.portals ?? []) if (p.kind === 'floor' && p.node) out.add(p.node);
  for (const r of Object.values(rooms.rooms))
    if (r.scene === m.scene_id) for (const l of Object.values(r.portals ?? {})) if (l.geometry?.kind === 'floor' && l.geometry.node) out.add(l.geometry.node);
  return out;
}

export class Portals {
  list: ActivePortal[] = [];
  /** Portals the player must leave before they can fire again (just arrived through one). */
  private disarmed = new Set<string>();
  private pickMat = new MeshBasicMaterial({ colorWrite: false, depthWrite: false });
  private markerMat = new MeshBasicMaterial({ color: 0x1c1915 });

  constructor(private scene: Scene) {}

  /**
   * Portals of the room now shown. `flags`: the URL flags that are on. A manifest portal with `marker: true` (a
   * scene without door geometry, e.g. the placeholder) gets a dark doorway drawn on the wall.
   */
  set(m: Manifest, info: RoomInfo, flags: (f: string) => boolean): void {
    this.clear();
    const ids = new Set([...(m.portals ?? []).map((p) => p.id), ...Object.keys(info.portals)]);
    for (const id of ids) {
      const p = m.portals?.find((x) => x.id === id) ?? geometryPortal(id, info);
      const link: PortalLink | null = info.portals[id] ?? (p?.to_room ? { to_room: p.to_room, to_spawn: p.to_spawn } : null);
      // a closed door (a room connection set, room/experience.ts): no portal; the scene's door stays as built
      if (!p || !link || link.closed || (link.flag && !flags(link.flag))) continue;
      const position = new Vector3(...p.position);
      if (p.kind === 'floor' && p.area) {
        // pick box over the area, from the floor up to the ladder's handholds
        const [x0, z0] = p.area.min, [x1, z1] = p.area.max;
        const h = p.height ?? FLOOR_PICK_H;
        const mesh = new Mesh(new BoxGeometry(x1 - x0, h, z1 - z0), this.pickMat);
        mesh.name = `portal_${id}`;
        mesh.userData.interactive = PORTAL_PREFIX + id;
        mesh.position.set((x0 + x1) / 2, position.y + h / 2, (z0 + z1) / 2);
        mesh.matrixAutoUpdate = false;
        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);
        this.scene.add(mesh);
        this.list.push({ id, key: PORTAL_PREFIX + id, position, normal: new Vector3(0, 1, 0), tangent: new Vector3(1, 0, 0),
          size: [x1 - x0, z1 - z0], link, mesh, marker: null, floor: { x0, z0, x1, z1, reach: p.reach ?? FLOOR_REACH_M },
          node: p.node ?? null });
        continue;
      }
      const normal = new Vector3(...p.normal).normalize();
      const tangent = new Vector3(0, 1, 0).cross(normal).normalize();
      const [w, h] = p.size;
      // pick box: the opening, 2 cm deep, its front 1 cm into the room
      const mesh = new Mesh(new BoxGeometry(w, h, 0.02), this.pickMat);
      mesh.name = `portal_${id}`;
      mesh.userData.interactive = PORTAL_PREFIX + id; // Interaction reads the id of a hit here
      mesh.position.set(position.x, position.y + h / 2, position.z);
      mesh.lookAt(mesh.position.clone().add(normal));
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.updateMatrixWorld(true);
      this.scene.add(mesh);
      let marker: Mesh | null = null;
      if (p.marker) {
        marker = new Mesh(new BoxGeometry(w, h, 0.004), this.markerMat);
        marker.name = `portal_${id}_doorway`;
        marker.position.copy(mesh.position).addScaledVector(normal, 0.003);
        marker.quaternion.copy(mesh.quaternion);
        marker.matrixAutoUpdate = false;
        marker.updateMatrix();
        marker.updateMatrixWorld(true);
        this.scene.add(marker);
      }
      this.list.push({ id, key: PORTAL_PREFIX + id, position, normal, tangent, size: [w, h], link, mesh, marker, floor: null, node: null });
    }
  }

  /** Interactive targets (Interaction.setScene adds them to the room's own). */
  interactive(): Map<string, Mesh[]> {
    return new Map(this.list.map((p) => [p.key, [p.mesh]]));
  }

  /** Outline shapes by interactive id: each floor portal's ladder, from the loaded scene's shapes. */
  shapes(shapes: Map<string, Mesh[]>): Map<string, Mesh[]> {
    const out = new Map<string, Mesh[]>();
    for (const p of this.list) {
      const s = p.node ? shapes.get(p.node) : undefined;
      if (s?.length) out.set(p.key, s);
    }
    return out;
  }

  byKey(key: string): ActivePortal | undefined {
    return this.list.find((p) => p.key === key);
  }

  /** After arriving (or spawning) at x, z: portals whose trigger zone holds the player fire only after leaving it. */
  disarmAt(x: number, z: number): void {
    this.disarmed = new Set(this.list.filter((p) => this.inZone(p, x, z, 0.15)).map((p) => p.id));
  }

  /** The door (wall portal) the player walked into this frame (position x, z, velocity vx, vz), or null. */
  check(x: number, z: number, vx: number, vz: number): ActivePortal | null {
    for (const p of this.list) {
      if (this.disarmed.has(p.id)) {
        if (!this.inZone(p, x, z, 0.15)) this.disarmed.delete(p.id);
        continue;
      }
      // floor portals open only by click or tap, never by walking into them
      if (p.floor) continue;
      // moving toward the wall (against the normal)
      if (vx * p.normal.x + vz * p.normal.z >= -0.05) continue;
      if (this.inZone(p, x, z, 0)) return p;
    }
    return null;
  }

  /** In the portal's trigger zone, grown by `extra` (m). */
  private inZone(p: ActivePortal, x: number, z: number, extra: number): boolean {
    if (p.floor) {
      const f = p.floor;
      return Math.hypot(Math.max(f.x0 - x, 0, x - f.x1), Math.max(f.z0 - z, 0, z - f.z1)) < f.reach + extra;
    }
    const depth = TRIGGER_M + extra;
    const dx = x - p.position.x, dz = z - p.position.z;
    const d = dx * p.normal.x + dz * p.normal.z;
    const s = dx * p.tangent.x + dz * p.tangent.z;
    return d < depth && d > -0.5 && Math.abs(s) < p.size[0] / 2 - 0.05;
  }

  clear(): void {
    for (const p of this.list) {
      p.mesh.removeFromParent();
      p.mesh.geometry.dispose();
      if (p.marker) { p.marker.removeFromParent(); p.marker.geometry.dispose(); }
    }
    this.list = [];
    this.disarmed.clear();
  }
}
